import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { unitId } from '@/annotations'
import { Button } from '@/components/Button'
import { IconButton } from '@/components/IconButton'
import { Note } from '@/components/Note'
import { getApiClient, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { useActiveOrgId, useActiveWorkspaceId } from '@/data/store'
import { REPORTS } from '@/layers/report/reportConfig'
import { GridTable } from '@/components/GridTable'
import { Lozenge } from '@/components/Lozenge'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'

type RestorePoint = components['schemas']['RestorePoint']
type ReportCatalogue = components['schemas']['ReportCatalogue']

// Prototype_v2.html `#v-health`'s own `.filterrow` `<select>` — the seven categories
// `design/28-report-catalogue.md` §28.2 groups the whole catalogue into, closed and stable.
const CATEGORIES = ['Access', 'Branch protection', 'Repository activity', 'Pipelines', 'Storage', 'File types', 'Users'] as const

/**
 * `v-health` — process/09-SCREEN-SPECS/v-health.md. The report index. The picker shows each
 * configuration snapshot's handle and date together (rule 1). `Export reports` has no operation
 * (`D89`) — the combined export §28.1 gestures at is undecided per the spec itself. **W148**: each
 * row's title is now a real
 * link into `ReportLayer` (`/reports/<unit-id>`) — resolved by matching this row's own `title`
 * against `REPORTS`' hand-authored join, the reverse of `ReportLayer`'s own lookup.
 *
 * **`panel`/`grid-tools`/`grid-footer` added W158**, wrapping structure this screen already had
 * rather than new content — plus one real addition, a "Refresh" `icon-button` invalidating the
 * catalogue query. No `data-act`: the spec's own Actions table is empty.
 *
 * **`grid-filter-row` built W180** — the prototype's own two filters (`.filterrow`'s category
 * `<select>` and a health-parameter text search) filter this screen's own already-fully-fetched,
 * unpaged catalogue client-side, the same "no server parameter exists, filter what is already
 * completely in hand" pattern `VConnected.tsx` already established at W168 —
 * `reportListCatalogue`'s only query parameter is `snapshot_job_id`, confirmed against the
 * generated schema before choosing this over inventing a server parameter. **Table styling
 * corrected to the shared `tableStyles.ts` constants in the same pass** — every table in this app
 * had been hand-styled as plain body text with a border, never the prototype's own `.grid-tbl`
 * header/zebra/hover treatment; see that file's own doc comment for the full account.
 *
 * **Object type column was plain text; the prototype's own `span.lz.t` is a `lozenge`.** Fixed by
 * wrapping `r.category` in the shared `Lozenge` component (`variant="t"`), found missing against a
 * visual-diff sweep of `Prototype_v2.html`. **Column widths were unset**, so an auto-layout table
 * gave `Health parameter` too little room (titles wrapped two lines) and `Description` too much
 * (full prose, no truncation) — the prototype fixes `Object type`/`Count` to a pixel width each and
 * truncates `Description` to one line with an ellipsis (`td.trunc`); replicated here via
 * `thClassName`/`tdClassName` rather than a global `table-layout: fixed` change to the shared
 * `GridTable`, which every other screen also renders through.
 *
 * **A brand-new workspace (no configuration backup has completed yet) rendered a near-blank panel
 * — found live, 2026-09-25.** With `points.data` empty, the snapshot picker had no options and
 * `jobId` stayed `''`, so `catalogue` (`enabled: Boolean(jobId)`) never ran and `GridTable`'s own
 * `emptyMessage` fell back to *"No reports match these filters"* — correct wording for a filtered-
 * out result set, wrong and actively confusing for "nothing has ever run here yet," a different
 * claim entirely. `EMPTY_NO_SNAPSHOT` names the real reason and links straight to the one action
 * that resolves it, the same "say why, don't just look broken" rule the rest of this pass already
 * follows for served `Observation` states.
 *
 * **Count column removed 2026-09-26 (OI-C97 frontend half, Sanket's own call).** `reportListCatalogue`
 * no longer computes `finding_count` at all — it now always serves `NOT_CAPTURED` for every row,
 * because computing it here ran every report's own Athena-backed `compute()` just for this landing
 * grid, which is exactly what caused a live timeout. Real counts still exist and still render, just
 * one level down: opening a report (`ReportLayer`) hits `reportGet` for that report alone, real data
 * as always. Deliberately not replaced with a background fetch of all ~21 reports right after the
 * list renders — that would reintroduce the same compute cost, just deferred instead of removed.
 */
export function VHealth() {
  const orgId = useActiveOrgId()!
  const workspaceId = useActiveWorkspaceId()!
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [manualJobId, setManualJobId] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [titleFilter, setTitleFilter] = useState('')

  const points = useQuery({
    queryKey: ['config-restore-points', orgId, workspaceId],
    // Found live, 2026-09-25: with no guard here, a workspace switch mid-visit (`setActiveOrg`,
    // `data/store.ts`, briefly nulls `activeWorkspaceId` while a new one is chosen) fired this
    // query with `workspace_id: null` — openapi-fetch leaves an unmatched `{workspace_id}` path
    // placeholder literally in the URL rather than substituting `null`, and the API 500'd on it.
    enabled: Boolean(orgId && workspaceId),
    queryFn: async (): Promise<readonly RestorePoint[]> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/snapshots', {
        params: { path: { org_id: orgId, workspace_id: workspaceId }, query: { kind: 'CONFIG_BACKUP', include_expired: true } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return (data?.items ?? []) as unknown as readonly RestorePoint[]
    },
  })

  // The newest snapshot is the default selection, computed in render rather than seeded via an
  // effect — the same pattern `TargetObjectTypesPane` (W146) uses for the same reason: no
  // cascading render, and it stays correct if the list resolves after the first paint.
  const jobId = manualJobId || points.data?.[0]?.data_snapshot.job_id || ''

  const catalogue = useQuery({
    queryKey: ['report-catalogue', orgId, workspaceId, jobId],
    enabled: Boolean(jobId),
    queryFn: async (): Promise<ReportCatalogue> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/reports', {
        params: { path: { org_id: orgId, workspace_id: workspaceId }, query: { snapshot_job_id: jobId } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as ReportCatalogue
    },
  })

  const problem = catalogue.error instanceof ApiProblemError ? catalogue.error.problem : undefined
  const hasFilters = Boolean(categoryFilter || titleFilter)
  const rows = (catalogue.data?.reports ?? []).filter(
    (r) => (!categoryFilter || r.category === categoryFilter) && (!titleFilter || r.title.toLowerCase().includes(titleFilter.toLowerCase())),
  )
  const noSnapshotYet = points.isSuccess && points.data.length === 0

  return (
    <div data-scr={unitId('v-health')}>
      <div data-cmp="panel" className="bg-panel border border-bd rounded-2">
        <div data-cmp="panel-header" className="flex items-center gap-5 p-6 border-b border-bd">
          <h1 className="m-0 text-section font-semibold flex-1">Workspace health</h1>
          {!noSnapshotYet && (
            <div data-cmp="grid-tools" className="flex items-center gap-4">
              <IconButton
                data-act={undefined}
                icon="i-refresh"
                label="Refresh"
                disabled={catalogue.isFetching}
                onClick={() => queryClient.invalidateQueries({ queryKey: ['report-catalogue', orgId, workspaceId, jobId] })}
              />
              <Button
                data-act={undefined}
                ghost
                disabled={!hasFilters}
                onClick={() => {
                  setCategoryFilter('')
                  setTitleFilter('')
                }}
              >
                Reset filters
              </Button>
              <Button data-act={undefined} ghost disabled title="No export exists for this list (D89)">
                Export reports
              </Button>
            </div>
          )}
        </div>

        {noSnapshotYet ? (
          <div className="py-16 px-8 text-center">
            <h3 className="m-0 mb-3 text-body-lg font-semibold">No health reports yet</h3>
            <p className="m-0 mb-7 mx-auto text-body text-tx2" style={{ maxWidth: 480 }}>
              Workspace health is read from your most recent configuration backup, and none has completed for this workspace yet. Every
              parameter on this page will appear automatically once one does.
            </p>
            <Button data-act={undefined} onClick={() => navigate('/backup')}>
              Back up now
            </Button>
          </div>
        ) : (
          <>
            <div className="p-6 pb-0">
              <label className="block mb-6 text-small font-semibold text-tx2" style={{ maxWidth: 420 }}>
                View all health parameters as of configuration snapshot
                <select value={jobId} onChange={(e) => setManualJobId(e.target.value)} className="block w-full mt-2 border border-bd2 rounded-1 py-3 px-4 text-body-lg">
                  {(points.data ?? []).map((p) => (
                    <option key={p.data_snapshot.job_id} value={p.data_snapshot.job_id}>
                      {p.data_snapshot.handle} — {p.started_at ? p.started_at.display : p.data_snapshot.job_id}
                    </option>
                  ))}
                </select>
              </label>

              {problem && (isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <Note>{problem.title}</Note>)}
            </div>

            <div data-cmp="grid-filter-row" className="flex gap-4 px-6 pb-4">
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="border-0 border-b border-bd2 bg-transparent py-2 text-small text-tx2 outline-none"
                style={{ maxWidth: 220 }}
              >
                <option value="">Filter by object type</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <input
                value={titleFilter}
                onChange={(e) => setTitleFilter(e.target.value)}
                placeholder="Filter by health parameter"
                className="flex-1 border-0 border-b border-bd2 bg-transparent py-2 text-small text-tx2 outline-none placeholder:text-muted"
              />
            </div>

            <GridTable
              columns={[
                { key: 'objectType', header: 'Object type', thClassName: 'w-[140px]', tdClassName: 'whitespace-nowrap', render: (r) => <Lozenge variant="t">{r.category}</Lozenge> },
                {
                  key: 'parameter',
                  header: 'Health parameter',
                  thClassName: 'min-w-[260px]',
                  render: (r) => {
                    const target = REPORTS.find((rep) => rep.title === r.title)
                    return target ? (
                      <button type="button" data-cmp="link" className="underline text-link bg-transparent border-0 p-0 cursor-pointer text-body-lg" onClick={() => navigate(`/reports/${target.unitId}`)}>
                        {r.title}
                      </button>
                    ) : (
                      r.title
                    )
                  },
                },
                { key: 'description', header: 'Description', tdClassName: 'max-w-[1px] whitespace-nowrap overflow-hidden text-ellipsis', render: (r) => r.description ?? '—' },
              ]}
              rows={rows}
              getRowKey={(r) => r.report_key}
              emptyMessage={catalogue.isLoading ? '' : 'No reports match these filters.'}
            />

            <div data-cmp="grid-footer" className="border-t border-bd flex items-center justify-between p-6">
              <Note>Every workspace-scoped report in the catalogue — not paged; the two filters above run client-side over the complete list.</Note>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
